import type { EligibilityWarning } from "../types";

/**
 * Which field an eligibility warning belongs beside.
 *
 * ⚠️ **A mirror, and a deliberate one.** `eligibility_warnings[]` carries `code` and `section` but
 * no field id, while the design puts each warning inline against the answer that caused it — so
 * something has to bridge the two, and the server does not.
 *
 * It is safe in a way the `EDUCATION_LEVEL_CODES` mirror was not: an unmapped code does not vanish,
 * it renders at the top of the step instead (see `partitionWarnings`). A new backend warning is
 * therefore visible on day one and merely less precisely placed — never lost, and never able to
 * break a write.
 *
 * The ask for Aymen: put `field_id` on the warning, and this file deletes itself.
 */
const WARNING_FIELD: Record<string, string> = {
  AGE: "age",
  WILAYA: "wilaya_code",
  RESIDENCY: "residency_confirmed",
  EDUCATION: "education_level_code",
  /**
   * Verified server-side (`warning_sections`, `application_services.py`): the ONE project-section
   * code, raised jointly by `registration_status_code` and `revenue_range_code` together (a
   * registered venture past a revenue threshold). Placed beside registration, the more direct of
   * the two triggers — the safe fallback covers the rest if that placement ever feels wrong.
   */
  VENTURE_COMMERCIAL: "registration_status_code",
};

/**
 * Split warnings into "beside this field" and "nowhere in particular".
 *
 * The second bucket is the escape hatch that makes the map above safe: anything unrecognised, or
 * belonging to another tab, still reaches the applicant.
 */
export function partitionWarnings(
  warnings: EligibilityWarning[],
  sectionKey: string
): { byField: Map<string, EligibilityWarning[]>; loose: EligibilityWarning[] } {
  const byField = new Map<string, EligibilityWarning[]>();
  const loose: EligibilityWarning[] = [];

  for (const warning of warnings) {
    if (warning.section !== sectionKey) continue;
    const field = WARNING_FIELD[warning.code];
    if (field === undefined) {
      loose.push(warning);
      continue;
    }
    const list = byField.get(field);
    if (list) list.push(warning);
    else byField.set(field, [warning]);
  }

  return { byField, loose };
}
